import { useTeamOverviewEvents as useTeamOverviewEventsQuery } from 'app/frontend/base/config/queries'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

type OverviewDirection = 'before' | 'after'
type OverviewSeed = {
  cursor?: number
  dir?: OverviewDirection
}

const parseEventId = (value: string | null) => {
  if (!value) return undefined
  const id = Number(value)
  if (!Number.isInteger(id) || id <= 0) return undefined
  return id
}

const parseDirection = (
  value: string | null,
): OverviewDirection | undefined => {
  if (value === 'before' || value === 'after') return value
  return undefined
}

const sameIds = (left: number[], right: number[]) => {
  if (left.length !== right.length) return false
  return left.every((value, index) => value === right[index])
}

export const useTeamOverviewEvents = (
  teamId: number,
  maxVisibleEvents: number,
) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const initialSeedRef = useRef<OverviewSeed | null>(null)
  if (initialSeedRef.current === null) {
    initialSeedRef.current = {
      cursor: parseEventId(searchParams.get('cursor')),
      dir: parseDirection(searchParams.get('dir')),
    }
  }
  const [seed, setSeed] = useState<OverviewSeed>(initialSeedRef.current)
  const [resetVersion, setResetVersion] = useState(0)
  const [pendingDirection, setPendingDirection] =
    useState<OverviewDirection | null>(null)
  const [settledDirection, setSettledDirection] = useState<
    OverviewDirection | undefined
  >(seed.dir)
  const defaultVisibleIdsRef = useRef<number[] | null>(null)

  const {
    data: overviewPages,
    isLoading: isLoadingEvents,
    isFetching,
  } = useTeamOverviewEventsQuery(teamId, {
    limit: 15,
    initialCursor: seed.cursor,
    initialDirection: seed.dir,
    resetVersion: resetVersion,
  })
  const currentPage = overviewPages?.pages[0]
  const events = currentPage?.events ?? []
  const visibleLimit = maxVisibleEvents > 0 ? maxVisibleEvents : 1
  const sliceDirection = isFetching ? settledDirection : seed.dir
  const visibleEvents = useMemo(() => {
    if (events.length <= visibleLimit) return events
    if (sliceDirection === 'before') return events.slice(-visibleLimit)
    return events.slice(0, visibleLimit)
  }, [events, visibleLimit, sliceDirection])
  const visibleIds = visibleEvents.map((event) => event.id)
  const showBackToNow = defaultVisibleIdsRef.current
    ? !sameIds(defaultVisibleIdsRef.current, visibleIds)
    : Boolean(seed.cursor || seed.dir)
  const isFetchingEvents = isFetching
  const hasHiddenBefore =
    sliceDirection === 'before' && events.length > visibleEvents.length
  const hasHiddenAfter =
    sliceDirection !== 'before' && events.length > visibleEvents.length
  const canNavigateLeft = Boolean(currentPage?.hasPrevPage) || hasHiddenBefore
  const canNavigateRight = Boolean(currentPage?.hasNextPage) || hasHiddenAfter

  const setCursorSearchParams = (
    next: { cursor: number; dir: OverviewDirection } | null,
  ) => {
    setSearchParams(
      (prev) => {
        const nextParams = new URLSearchParams(prev)
        if (!next) {
          nextParams.delete('cursor')
          nextParams.delete('dir')
          return nextParams
        }
        nextParams.set('cursor', next.cursor.toString())
        nextParams.set('dir', next.dir)
        return nextParams
      },
      { replace: true },
    )
  }

  const navigateLeft = () => {
    if (!canNavigateLeft || isFetchingEvents) return
    const anchorId = visibleEvents[0]?.id
    if (!anchorId) return
    setCursorSearchParams({ cursor: anchorId, dir: 'before' })
    setSeed({ cursor: anchorId, dir: 'before' })
    setResetVersion((value) => value + 1)
    setPendingDirection('before')
  }

  const navigateRight = () => {
    if (!canNavigateRight || isFetchingEvents) return
    const anchorId = visibleEvents[visibleEvents.length - 1]?.id
    if (!anchorId) return
    setCursorSearchParams({ cursor: anchorId, dir: 'after' })
    setSeed({ cursor: anchorId, dir: 'after' })
    setResetVersion((value) => value + 1)
    setPendingDirection('after')
  }

  const backToNow = () => {
    setCursorSearchParams(null)
    setSeed({})
    setResetVersion((value) => value + 1)
    setPendingDirection(null)
  }

  useEffect(() => {
    if (!isFetchingEvents) {
      setPendingDirection(null)
      setSettledDirection(seed.dir)
    }
  }, [isFetchingEvents, seed.dir])

  useEffect(() => {
    if (!isFetchingEvents && !seed.cursor && !seed.dir) {
      defaultVisibleIdsRef.current = visibleIds
    }
  }, [isFetchingEvents, seed.cursor, seed.dir, visibleIds])

  return {
    events,
    visibleEvents,
    isLoadingEvents,
    isFetchingEvents,
    showBackToNow,
    disableLeftButton: isFetchingEvents || !canNavigateLeft,
    disableRightButton: isFetchingEvents || !canNavigateRight,
    showLeftLoader: isFetchingEvents && pendingDirection === 'before',
    showRightLoader: isFetchingEvents && pendingDirection === 'after',
    navigateLeft,
    navigateRight,
    backToNow,
  }
}
